/**
 * Ask Atlas context type and suggestion builders per screen kind.
 * (doc 147 §9 Phase 4 — per-screen suggestions, not just per-entity.)
 *
 * Discriminated union of screen contexts that provide enough detail to
 * build context-aware example prompts. Each kind includes the minimal
 * required data for suggestion-building (e.g., entity id for entity,
 * domain id for catalog-domain, etc.).
 */

export type AskAtlasContext =
  | { kind: 'entity'; entityId: string }
  | { kind: 'sandbox'; sandboxId?: string }
  | { kind: 'simulate'; focusEntityId?: string }
  | { kind: 'catalog'; query?: string }
  | { kind: 'catalog-domain'; domainId: string }
  | { kind: 'dashboard' }
  | null;

/** Generic prompts shown when no screen context is provided. */
const GENERIC_PROMPTS = [
  'What writes to the Holding entity?',
  'Which entities carry PII and have no legal basis?',
  'List all GDPR Art.17 compliance gaps',
  'Which cloud functions emit Tier 0 events?',
];

/** Entity-focused prompts — shown when context.kind === "entity". */
function buildEntityPrompts(entityId: string): string[] {
  return [
    `What writes to ${entityId}?`,
    `What would break if I renamed a field on ${entityId}?`,
    `Has ${entityId} changed recently?`,
    `Which surfaces read ${entityId}?`,
  ];
}

/** Sandbox-focused prompts — shown when context.kind === "sandbox". */
function buildSandboxPrompts(): string[] {
  return [
    'What entity does this draft affect most?',
    'Does this proposal introduce any cycles?',
    'How would this change impact downstream surfaces?',
    'Are there any naming inconsistencies in this draft?',
  ];
}

/** Simulate-focused prompts — shown when context.kind === "simulate". */
function buildSimulatePrompts(): string[] {
  return [
    'What domains does this scenario cover?',
    'Are there any uncovered entities in this simulation?',
    'What would break if I tweaked this scenario?',
    'Which system boundaries does this trace cross?',
  ];
}

/** Catalog-focused prompts — shown when context.kind === "catalog". */
function buildCatalogPrompts(): string[] {
  return [
    'Which domains have the most uncovered entities?',
    'What is the health score distribution across domains?',
    'Which entities appear in multiple domains?',
    'Are there any orphaned entities in the catalog?',
  ];
}

/** Catalog-domain-focused prompts — shown when context.kind === "catalog-domain". */
function buildCatalogDomainPrompts(domainId: string): string[] {
  return [
    `What entities in ${domainId} have no incoming references?`,
    `How tightly coupled are entities in ${domainId}?`,
    `Which external domains does ${domainId} depend on?`,
    `Are there any design anti-patterns in ${domainId}?`,
  ];
}

/** Dashboard-focused prompts — shown when context.kind === "dashboard". */
function buildDashboardPrompts(): string[] {
  return [
    'What changed in the model this week?',
    'Which recent edits introduced new gaps?',
    'What is the team currently working on?',
    'Are there any pending issues to review?',
  ];
}

/**
 * Build example-prompt suggestions based on screen context.
 * Returns generic prompts if context is null.
 */
export function buildSuggestions(context: AskAtlasContext): string[] {
  if (context === null) return GENERIC_PROMPTS;
  switch (context.kind) {
    case 'entity':
      return buildEntityPrompts(context.entityId);
    case 'sandbox':
      return buildSandboxPrompts();
    case 'simulate':
      return buildSimulatePrompts();
    case 'catalog':
      return buildCatalogPrompts();
    case 'catalog-domain':
      return buildCatalogDomainPrompts(context.domainId);
    case 'dashboard':
      return buildDashboardPrompts();
  }
}
